import React, { useRef, useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { logout, selectUser } from "../../../store/authSlice";
import "./EditProfile.css";
import api from "../../../axiosconfig.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faX } from "@fortawesome/free-solid-svg-icons";
import "react-toastify/dist/ReactToastify.css";
import { setProfileImagePath } from "../../../store/profileImageSlice.js";
import countries from "world-countries";
import ReactCountryFlag from "react-country-flag";
import { Select, MenuItem, ListSubheader, Box } from "@mui/material";
import {
  GENDER_OPTIONS,
  AGE_OPTIONS,
  VALID_GENDERS,
  VALID_AGES,
  normalizeValue,
  normalizeAge,
  normalizeGender,
} from "../../../constants/profileConstants.js";
import {
  fetchUserProfile,
  setProfileData,
  setProfileDraft,
  clearProfileDraft,
  clearProfile,
  selectActiveProfile,
} from "../../../store/profileSlice.js";

export const Editprofile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const activeProfile = useSelector(selectActiveProfile);
  const userEmail =
    activeProfile?.email && activeProfile.email.includes("@")
      ? activeProfile.email
      : user?.email || activeProfile?.email || "";

  const [image, setImage] = useState(() => activeProfile.profilePictureUrl || null);
  const [prevImage, setPrevImage] = useState(() => activeProfile.profilePictureUrl || null);
  const [isSuperUser, setIsSuperUser] = useState(() => user?.role === "SuperUser");

  // Prepare country list with dialCode attached
  const countryList = useMemo(() => {
    const allCountries = countries
      .map((c) => {
        const root = c.idd?.root || "";
        const suffix = c.idd?.suffixes && c.idd.suffixes.length === 1 ? c.idd.suffixes[0] : "";
        return {
          name: c.name.common,
          code: c.cca2,
          dialCode: root ? `${root}${suffix}` : "+1",
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
    
    // Put USA, Canada, Sri Lanka, UK, Australia, India at the top
    const priorityCodes = ["US", "CA", "LK", "GB", "AU", "IN"];
    const priority = [];
    priorityCodes.forEach((code) => {
      const found = allCountries.find((c) => c.code === code);
      if (found) priority.push(found);
    });

    const others = allCountries.filter((c) => !priorityCodes.includes(c.code));
    
    return [...priority, ...others];
  }, []);

  // Prepare dial code list with flags and names
  const dialCodeList = useMemo(() => {
    const all = countries
      .map((c) => {
        const root = c.idd?.root || "";
        const suffix = c.idd?.suffixes && c.idd.suffixes.length === 1 ? c.idd.suffixes[0] : "";
        const dial = root ? `${root}${suffix}` : "";
        return {
          code: c.cca2,
          name: c.name.common,
          dialCode: dial,
        };
      })
      .filter((c) => c.dialCode);

    const topCodes = ["US", "CA", "LK", "GB", "IN", "AU"];
    const top = [];
    topCodes.forEach((code) => {
      const found = all.find((c) => c.code === code);
      if (found) top.push(found);
    });

    const rest = all
      .filter((c) => !topCodes.includes(c.code))
      .sort((a, b) => a.name.localeCompare(b.name));

    return [...top, ...rest];
  }, []);

  // Helper to split raw phone into dial code and subscriber number
  const parsePhoneData = (rawPhone, defaultCountry) => {
    if (!rawPhone) {
      const countryMatch = countryList.find((c) => c.name === defaultCountry);
      return { dialCode: countryMatch?.dialCode || "+1", number: "" };
    }
    const trimmed = String(rawPhone).trim();
    const match = trimmed.match(/^(\+\d{1,4})\s*(.*)$/);
    if (match) {
      return { dialCode: match[1], number: match[2] };
    }
    const countryMatch = countryList.find((c) => c.name === defaultCountry);
    return { dialCode: countryMatch?.dialCode || "+1", number: trimmed };
  };

  const initialPhoneParsed = useMemo(
    () => parsePhoneData(activeProfile.phone, activeProfile.country),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [displayName, setDisplayName] = useState(() => activeProfile.displayName || "");
  const [firstName, setFirstName] = useState(() => activeProfile.firstName || "");
  const [lastName, setLastName] = useState(() => activeProfile.lastName || "");
  const [age, setAge] = useState(() => normalizeAge(activeProfile.age) || "");
  const [gender, setGender] = useState(() => normalizeGender(activeProfile.gender) || "");
  const [country, setCountry] = useState(() => activeProfile.country || "");
  const [language, setLanguage] = useState(() => activeProfile.language || "");
  const [dialCode, setDialCode] = useState(() => activeProfile.dialCode || initialPhoneParsed.dialCode || "+1");
  const [phone, setPhone] = useState(() => activeProfile.phoneNumber || initialPhoneParsed.number || "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({}); // Inline validation errors

  const API_URL = process.env.REACT_APP_API_URL;
  const FILESERVER_URL = process.env.REACT_APP_FILESERVER_URL;
  const PHOTOBUCKET_URL = process.env.REACT_APP_PHOTOBUCKET_URL;

  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");

  var warningModal = document.getElementById("warningModal");

  // Close modal on outside click
  window.onclick = function (event) {
    if (event.target === warningModal) {
      warningModal.style.display = "none";
    }
  };

  const handlePencilClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  // Sync state if activeProfile in Redux updates
  useEffect(() => {
    if (activeProfile) {
      if (activeProfile.displayName !== undefined) setDisplayName(activeProfile.displayName);
      if (activeProfile.firstName !== undefined) setFirstName(activeProfile.firstName);
      if (activeProfile.lastName !== undefined) setLastName(activeProfile.lastName);
      if (activeProfile.age !== undefined) setAge(normalizeAge(activeProfile.age) || "");
      if (activeProfile.gender !== undefined) setGender(normalizeGender(activeProfile.gender) || "");
      if (activeProfile.country !== undefined) setCountry(activeProfile.country);
      if (activeProfile.language !== undefined) setLanguage(activeProfile.language);
      if (activeProfile.dialCode !== undefined) {
        setDialCode(activeProfile.dialCode);
      } else if (activeProfile.phone !== undefined) {
        const parsed = parsePhoneData(activeProfile.phone, activeProfile.country);
        setDialCode(parsed.dialCode);
        setPhone(parsed.number);
      }
      if (activeProfile.phoneNumber !== undefined) {
        setPhone(activeProfile.phoneNumber);
      }
      if (activeProfile.profilePictureUrl && !preview) {
        setImage(activeProfile.profilePictureUrl);
        setPrevImage(activeProfile.profilePictureUrl);
      }
    }
  }, [
    activeProfile.displayName,
    activeProfile.firstName,
    activeProfile.lastName,
    activeProfile.age,
    activeProfile.gender,
    activeProfile.country,
    activeProfile.language,
    activeProfile.phone,
    activeProfile.dialCode,
    activeProfile.phoneNumber,
    activeProfile.profilePictureUrl,
    preview,
  ]);

  // Fetch fresh profile data in background on mount
  useEffect(() => {
    dispatch(fetchUserProfile());
    setIsSuperUser(user?.role === "SuperUser");
  }, [dispatch, user]);

  const handleLogout = async () => {
    try {
      await api.post(`${API_URL}/login`, { request: "logout" });
      localStorage.removeItem("completedAssessmentTabs");
      localStorage.removeItem("progress");
      dispatch(clearProfile());
      dispatch(logout());
      navigate("/");
    } catch (error) {
      console.log("Logout failed: ", error);
    }
  };

  const handleDeactivate = async () => {
    try {
      const requestType = isSuperUser ? "deactivateCompany" : "deactivateAccount";
      const request = await api.post(`${API_URL}/profile`, { request: requestType });
      if (request.data.result === 1) {
        toast.success("Your account has been successfully deactivated. You no longer have access to Mindsherpa.");
        toast.info("If you wish to return, please contact your organization's administrator to request reactivation.");
        toast.info("We’re sorry to see you go and hope to support your wellbeing journey again in the future.");
        handleLogout();
      } else {
        toast.error("Something went wrong trying to deactivate your account.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong trying to deactivate your account.");
    }
  };

  const resetFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const handleUploadProfilePicture = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!file) {
      toast.warning("Please select an image first.");
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append("profilePic", file);
    formData.append("userId", user.sub);


    try {
      const uploadRes = await fetch(`${FILESERVER_URL}/upload-profile`, {
        method: "POST",
        body: formData,
      });

      // Handle file size limit error (HTTP 413)
      if (uploadRes.status === 413) {
        toast.error("Unable to save your file. It exceeds the 5 MB limit. Please reduce the size and try again.", {
          autoClose: 5000,
          style: { backgroundColor: "#ffe4e4", color: "#b30000" },
        });
        // Revert UI
        setImage(prevImage);
        setFile(null);
        setPreview("");
        resetFileInput(); 
        setUploading(false);
        return;
      }

      const uploadData = await uploadRes.json();

      // Handle backend errors
      if (!uploadRes.ok || uploadData.error) {
        toast.error(uploadData.error || "Upload failed.");
        setImage(prevImage);
        setFile(null);
        setPreview("");
        resetFileInput();
        setUploading(false);
        return;
      }

      if (!uploadData.objectPath) {
        toast.error("Upload failed: missing object path.");
        setImage(prevImage);
        setFile(null);
        setPreview("");
        resetFileInput();
        setUploading(false);
        return;
      }

      // Save path in DB
      const apiRes = await api.post(`${API_URL}/profile`, {
        request: "setProfilePicture",
        objectPath: uploadData.objectPath,
      });

      if (apiRes.data?.result === 1) {
        const publicUrl = `${PHOTOBUCKET_URL}/${uploadData.objectPath}`;
        setImage(publicUrl);
        setPrevImage(publicUrl); 
        dispatch(setProfileImagePath(uploadData.objectPath));
        dispatch(setProfileData({ profilePictureUrl: publicUrl }));
        toast.success("Profile picture updated successfully!");
      } else {
        toast.error("Database update failed.");
        // revert visual since DB didn’t accept new path
        setImage(prevImage);
      }
    } catch (error) {
      console.error("Error uploading profile picture:", error);
      toast.error("Upload failed. Please try again.");
      setImage(prevImage);
    } finally {
      // Reset temporary states no matter what
      setFile(null);
      setPreview("");
      resetFileInput();
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);

    // Revoke old preview URL to avoid memory leak
    if (preview) URL.revokeObjectURL(preview);

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreview(objectUrl);
    setImage(objectUrl); // immediate local preview

    // allow re-selecting the same file again later
    e.target.value = null;
  };

  // cleanup the blob preview when it changes / unmounts
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const handleSave = async () => {
    // Reset errors
    const newErrors = {};

    // Validate phone (if provided) - allow flexible international lengths (7 to 15 digits)
    if (phone) {
      const digitsOnly = phone.replace(/\D/g, "");
      if (digitsOnly.length < 7 || digitsOnly.length > 15) {
        newErrors.phone = "Please enter a valid phone number (7 to 15 digits).";
      }
    }

    // Validate and normalize age (if provided)
    let normalizedAge = null;
    if (age && age !== "null" && age !== "none") {
      normalizedAge = normalizeAge(age);
      if (!normalizedAge) {
        newErrors.age = "Please select a valid age range.";
      }
    }

    // Validate and normalize gender (if provided)
    let normalizedGender = null;
    if (gender && gender !== "null" && gender !== "none") {
      normalizedGender = normalizeGender(gender);
      if (!normalizedGender) {
        newErrors.gender = "Please select a valid gender.";
      }
    }

    setErrors(newErrors);

    // Stop if there are errors
    if (Object.keys(newErrors).length > 0) {
      toast.warning("Please fix the errors before saving.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const fullPhoneNumber = phone.trim() ? `${dialCode} ${phone.trim()}` : "";

    const formData = {
      firstName,
      lastName,
      Name: displayName,
      age: normalizedAge || null,
      gender: normalizedGender || null,
      location: country, // Country is stored in location column
      language,
      phoneNumber: fullPhoneNumber,
    };

    setSaving(true);

    try {
      const updateRes = await updateProfileField(formData);
      if (!updateRes || updateRes.result !== 1) {
        toast.error(updateRes?.message || "Failed to update profile. Please try again.", {
          position: "top-right",
          autoClose: 3500,
        });
        return;
      }

      dispatch(
        setProfileData({
          displayName,
          firstName,
          lastName,
          age: normalizedAge || "",
          gender: normalizedGender || "",
          country,
          language,
          phone: fullPhoneNumber,
          dialCode,
          phoneNumber: phone,
        })
      );
      dispatch(clearProfileDraft());
      toast.success("Profile updated successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile. Please try again.", {
        position: "top-right",
        autoClose: 3500,
      });
    } finally {
      setSaving(false);
    }
  };

  const updateProfileField = async (formData) => {
    try {
      const response = await api.post("/profile", {
        request: "setUserData",
        ...formData,
      });
      return response.data;
    } catch (error) {
      console.error("Profile update error:", error);
      return { result: -1, message: error?.response?.data?.message || "Request failed" };
    }
  };

  return (
    <div className="mainProfile">
      <div className="Frame-110">
        <div className="Frame-111">
          <div className="edit-profile-photo">
            <img
              src={image ? image : "/pro.png"}
              className="ProfileImg"
              alt="Profile"
              onError={(e) => {
                e.target.onError = null;
                e.target.src = "/pro.png";
              }}
            />
            <div className="pencilsimple" onClick={handlePencilClick}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                className="icon-pen"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  fill="black"
                  d="M15.1328 4.21108L11.7889 0.866448C11.6777 0.755225 11.5457 0.666997 11.4004 0.606802C11.2551 0.546607 11.0994 0.515625 10.9421 0.515625C10.7848 0.515625 10.6291 0.546607 10.4838 0.606802C10.3385 0.666997 10.2065 0.755225 10.0953 0.866448L0.866634 10.0958C0.754957 10.2066 0.666415 10.3385 0.606158 10.4838C0.5459 10.6291 0.515128 10.785 0.515631 10.9423V14.2869C0.515631 14.6045 0.641791 14.9091 0.866357 15.1336C1.09092 15.3582 1.3955 15.4844 1.71308 15.4844H5.05772C5.21503 15.4849 5.37087 15.4541 5.51618 15.3938C5.6615 15.3336 5.79338 15.245 5.90417 15.1334L15.1328 5.90473C15.244 5.79354 15.3322 5.66152 15.3924 5.51622C15.4526 5.37092 15.4836 5.21518 15.4836 5.05791C15.4836 4.90063 15.4526 4.7449 15.3924 4.5996C15.3322 4.4543 15.244 4.32228 15.1328 4.21108ZM12.4902 6.85371L9.14553 3.50983L10.9417 1.71365L14.2863 5.05753L12.4902 6.85371Z"
                />
              </svg>
            </div>

            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />

            {file && (
              <button
                type="button"
                disabled={uploading}
                onClick={handleUploadProfilePicture}
                className="save-image-btn"
              >
                {uploading ? "Uploading..." : "Save Image"}
              </button>
            )}
          </div>

          <div className="profile-identity-fields">
            <div className="profile-field-item">
              <div className="frame-113">
                <label>First Name</label>
              </div>
              <input
                type="text"
                placeholder="First name"
                className="frame-1144"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  dispatch(setProfileDraft({ firstName: e.target.value }));
                }}
              />
            </div>

            <div className="profile-field-item">
              <div className="frame-113">
                <label>Last Name</label>
              </div>
              <input
                type="text"
                placeholder="Last name"
                className="frame-1144"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  dispatch(setProfileDraft({ lastName: e.target.value }));
                }}
              />
            </div>

            <div className="profile-field-item">
              <div className="frame-113">
                <label>Display Name</label>
              </div>
              <input
                type="text"
                placeholder="What should we call you?"
                className="frame-1144"
                value={displayName}
                onChange={(e) => {
                  setDisplayName(e.target.value);
                  dispatch(setProfileDraft({ displayName: e.target.value }));
                }}
              />
            </div>

            <div className="profile-field-item">
              <div className="frame-113">
                <label>Email Address</label>
                <span className="profile-readonly-badge">Account Email</span>
              </div>
              <div className="profile-readonly-input-wrapper">
                <input
                  type="email"
                  readOnly
                  disabled
                  value={userEmail}
                  placeholder="your.email@example.com"
                  className="frame-1144 profile-input-readonly"
                />
                <svg
                  className="profile-readonly-lock-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="Frame-114">
          <div className="frame-115">
            <div className="frame-117">
              <div className="frame-118">
                <label>Age</label>
              </div>
              <div className="frame-132">
                <select
                  value={age}
                  onChange={(e) => {
                    setAge(e.target.value);
                    setErrors({ ...errors, age: "" });
                    dispatch(setProfileDraft({ age: e.target.value }));
                  }}
                  className={`language-select ${errors.age ? "error" : ""}`}
                >
                  <option value="">
                    Select Age
                  </option>
                  {AGE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.age && (
                  <div className="fieldError">{errors.age}</div>
                )}
              </div>
            </div>
          </div>

          <div className="frame-116">
            <div className="frame-120">
              <div className="frame-121">
                <label>Gender</label>
              </div>
              <div className="frame-132">
                <select
                  value={gender}
                  onChange={(e) => {
                    setGender(e.target.value);
                    setErrors({ ...errors, gender: "" });
                    dispatch(setProfileDraft({ gender: e.target.value }));
                  }}
                  className={`language-select ${errors.gender ? "error" : ""}`}
                >
                  <option value="">
                    Select gender
                  </option>
                  {GENDER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.gender && (
                  <div className="fieldError">{errors.gender}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="Frame-123">
          <div className="Frame-1233">
            <div className="frame-124">
              <div className="frame-126">
                <label>Phone Number</label>
              </div>
              <div className="frame-127">
                <Select
                  value={dialCode}
                  onChange={(e) => {
                    setDialCode(e.target.value);
                    dispatch(setProfileDraft({ dialCode: e.target.value, phone: `${e.target.value} ${phone}` }));
                  }}
                  displayEmpty
                  renderValue={(selected) => {
                    const match = dialCodeList.find(
                      (c) => c.dialCode === selected && (!country || c.name === country)
                    ) || dialCodeList.find((c) => c.dialCode === selected);
                    return (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                        {match && (
                          <ReactCountryFlag
                            countryCode={match.code}
                            svg
                            style={{ width: "1.25em", height: "1.25em" }}
                          />
                        )}
                        <span style={{ fontWeight: 600, fontSize: "14px", color: "#334155" }}>
                          {selected || "+1"}
                        </span>
                      </Box>
                    );
                  }}
                  sx={{
                    height: "48px",
                    borderRadius: "10px",
                    background: "#f8fafc",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: errors.phone ? "#ef4444" : "#e2e8f0",
                      borderWidth: "1.5px",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#cbd5e1",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#0a7253",
                      borderWidth: "1.5px",
                    },
                    "& .MuiSelect-select": {
                      padding: "0 8px 0 10px !important",
                      display: "flex",
                      alignItems: "center",
                    },
                    minWidth: "108px",
                    flexShrink: 0,
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: { maxHeight: 320 },
                    },
                  }}
                >
                  {/* Priority Dial Codes */}
                  {dialCodeList.slice(0, 6).map((item, idx) => (
                    <MenuItem key={`top-${item.code}-${idx}`} value={item.dialCode}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                        <ReactCountryFlag
                          countryCode={item.code}
                          svg
                          style={{ width: "1.25em", height: "1.25em" }}
                        />
                        <span style={{ fontWeight: 600, minWidth: "42px" }}>{item.dialCode}</span>
                        <span style={{ color: item.code === "LK" ? "#0f172a" : "#334155", fontSize: "13.5px", fontWeight: item.code === "LK" ? 700 : 500 }}>
                          {item.name}
                        </span>
                      </Box>
                    </MenuItem>
                  ))}
                  <ListSubheader sx={{ fontSize: "11px", color: "#94a3b8", textAlign: "center", lineHeight: "24px" }}>
                    ────── All Other Dial Codes ──────
                  </ListSubheader>
                  {dialCodeList.slice(6).map((item, idx) => (
                    <MenuItem key={`rest-${item.code}-${idx}`} value={item.dialCode}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                        <ReactCountryFlag
                          countryCode={item.code}
                          svg
                          style={{ width: "1.2em", height: "1.2em" }}
                        />
                        <span style={{ fontWeight: 600, minWidth: "42px" }}>{item.dialCode}</span>
                        <span style={{ color: "#64748b", fontSize: "13px" }}>{item.name}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                <input
                  type="tel"
                  placeholder="XXXXXXXXXX"
                  className={`frame-129 ${errors.phone ? "error" : ""}`}
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    if (errors.phone) setErrors({ ...errors, phone: "" });
                    dispatch(setProfileDraft({ phone: `${dialCode} ${e.target.value}`, phoneNumber: e.target.value, dialCode }));
                  }}
                />
              </div>
              {errors.phone && <div className="fieldError">{errors.phone}</div>}
            </div>
          </div>

          <div className="frame-125">
            <div className="frame-130">
              <div className="frame-131">
                <label>Language</label>
              </div>
              <div className="frame-132">
                <select
                  value={language}
                  onChange={(e) => {
                    setLanguage(e.target.value);
                    dispatch(setProfileDraft({ language: e.target.value }));
                  }}
                  className="language-select"
                >
                  <option value="" disabled>Select language</option>
                  <option value="en">English</option>
                  <option value="fr">French</option>
                  <option value="pt">Portuguese</option>
                  <option value="es">Spanish</option>
                </select>
              </div>
            </div>
          </div>

          <div className="frame-125">
            <div className="frame-130">
              <div className="frame-131">
                <label>Country</label>
              </div>
              <div className="frame-132">
                <Select
                  value={country}
                  onChange={(e) => {
                    const selectedCountry = e.target.value;
                    setCountry(selectedCountry);
                    setErrors({ ...errors, country: "" });
                    dispatch(setProfileDraft({ country: selectedCountry }));

                    // Automatically update dial code if matching country found
                    const countryItem = countryList.find((c) => c.name === selectedCountry);
                    if (countryItem && countryItem.dialCode) {
                      setDialCode(countryItem.dialCode);
                      dispatch(setProfileDraft({ dialCode: countryItem.dialCode, phone: `${countryItem.dialCode} ${phone}` }));
                    }
                  }}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <em>Select country</em>;
                    }
                    const selectedCountry = countryList.find((c) => c.name === selected);
                    if (selectedCountry) {
                      return (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <ReactCountryFlag
                            countryCode={selectedCountry.code}
                            svg
                            style={{
                              width: "1.5em",
                              height: "1.5em",
                            }}
                          />
                          {selectedCountry.name}
                        </Box>
                      );
                    }
                    return selected;
                  }}
                  sx={{
                    width: '100%',
                    border: 'none',
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '& .MuiSelect-select': {
                      padding: 0,
                      color: '#64748b',
                      fontFamily: 'Roboto',
                      fontSize: '16px',
                      fontWeight: 400,
                      lineHeight: '28px',
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Select country</em>
                  </MenuItem>
                  
                  {/* Primary Countries: USA, Canada, Sri Lanka, UK, Australia, India */}
                  {countryList.slice(0, 6).map((countryItem) => (
                    <MenuItem key={`pri-${countryItem.code}`} value={countryItem.name}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <ReactCountryFlag
                          countryCode={countryItem.code}
                          svg
                          style={{
                            width: "1.5em",
                            height: "1.5em",
                          }}
                        />
                        <span style={{ fontWeight: countryItem.code === "LK" ? 700 : 500 }}>
                          {countryItem.name}
                        </span>
                      </Box>
                    </MenuItem>
                  ))}
                  
                  <ListSubheader sx={{ fontSize: '11px', color: '#94a3b8', textAlign: 'center', lineHeight: '24px' }}>
                    ────── All Other Countries ──────
                  </ListSubheader>
                  
                  {/* All other countries */}
                  {countryList.slice(6).map((countryItem) => (
                    <MenuItem key={countryItem.code} value={countryItem.name}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <ReactCountryFlag
                          countryCode={countryItem.code}
                          svg
                          style={{
                            width: "1.5em",
                            height: "1.5em",
                          }}
                        />
                        {countryItem.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.country && (
                  <div className="fieldError">{errors.country}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="button-section">
          <button
            type="button"
            className="save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <span className="save-btn-loading">
                <span className="save-spinner" />
                Saving...
              </span>
            ) : (
              "Save Changes"
            )}
          </button>
          <button
            type="button"
            className="deactivate-btn"
            onClick={() => (warningModal.style.display = "flex")}
            disabled={saving}
          >
            Deactivate Account
          </button>
        </div>
      </div>

      <div className="profile-modal-container" id="warningModal">
        <div className="profile-modal-body">
          <div
            className="profile-modal-close"
            onClick={() => (warningModal.style.display = "none")}
          >
            <FontAwesomeIcon icon={faX} />
          </div>
          <div className="profile-modal-header-text">
            Are you sure you want to deactivate your account?
          </div>
          <div
            className="profile-modal-body-text-container"
            style={{ display: isSuperUser ? "none" : "flex" }}
          >
            <div className="profile-modal-body-text-h1">
              Once deactivated, you'll lose access to all your data and tools.
            </div>
            <p>
              Reactivating will require contacting your organization's
              administrator, and this process may take time.
            </p>
            <p>
              We'd truly hate to see you go—your wellbeing journey matters to
              us. If something isn't working for you, please consider reaching
              out to{" "}
              <a href="mailto:info@mindsherpa.com">info@mindsherpa.health</a>{" "}
              first so we can help.
            </p>
          </div>
          <div
            className="profile-modal-body-text-container"
            style={{ display: isSuperUser ? "flex" : "none" }}
          >
            <div className="profile-modal-body-text-h1">
              Once deactivated, you'll lose access to all your data and tools.
            </div>
            <div className="profile-modal-body-text-h2">
              Since you are the organization's super user, all user accounts in
              your organization will be deactivated.
            </div>
            <p>
              Reactivating will require contacting Mindsherpa's support and this
              process may take time.
            </p>
            <p>
              We'd truly hate to see you go—your wellbeing journey matters to
              us. If something isn't working for you, please consider reaching
              out to{" "}
              <a href="mailto:info@mindsherpa.com">info@mindsherpa.health</a>{" "}
              first so we can help.
            </p>
          </div>
          <div className="modal-button-section">
            <button
              type="button"
              className="back-btn"
              onClick={() => (warningModal.style.display = "none")}
            >
              Keep My Account
            </button>
            <button
              type="button"
              className="deactivate-btn"
              onClick={handleDeactivate}
            >
              Deactivate Account
            </button>
          </div>
        </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};
